import React, { useEffect, useState, useCallback } from "react";
import { X, LogOut, ChevronUp, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMeetingStore } from "../store/meeting.store";
import { useAuth } from "../context/AuthProvider";
import { useSidebarItems } from "../hooks/useSidebarItems";
import { menuConfig, type SidebarItem as MenuSidebarItem } from "./menuConfig";
import clsx from "clsx";
import api from "../lib/api";
import { socket } from "./mediasoup/socket";
import { toast } from "react-toastify";
import type { User } from "../types";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  user: User;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  onLogout,
  user,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setUser } = useAuth();

  const causeListShow = useMeetingStore((s) => s.causeListShow);
  const setCauseListShow = useMeetingStore((s) => s.setCauseListShow);

  const [activeItem, setActiveItem] = useState("");
  const [childActiveItem, setChildActiveItem] = useState("");
  const [expandedItem, setExpandedItem] = useState("");

  const sidebarItems = useSidebarItems(
    user?.role,
    user?.delegation,
    menuConfig,
  );

  /* ---------------- ACTIVE ITEM ---------------- */

  useEffect(() => {
    const isRouteMatch = (path?: string) => {
      if (!path) return false;

      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return (
        pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`)
      );
    };

    if (pathname === "/stream") {
      setActiveItem("search-sessions");
      setChildActiveItem("");
      return;
    }

    for (const item of sidebarItems as MenuSidebarItem[]) {
      const activeChild = item.children?.find((child) =>
        isRouteMatch(child.path),
      );

      if (activeChild) {
        setActiveItem(item.id);
        setChildActiveItem(activeChild.id);
        setExpandedItem(item.id);
        return;
      }

      if (isRouteMatch(item.path)) {
        setActiveItem(item.id);
        setChildActiveItem("");
        return;
      }
    }

    setActiveItem("");
    setChildActiveItem("");
  }, [pathname, sidebarItems]);

  /* ---------------- ACTIONS ---------------- */

  const [loading, setLoading] = useState(false);

  const handleDelegationToggle = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const res = await api.post("/api/judge/toggle-reader-delegation");

      if (res?.data?.status) {
        const updatedDelegation = res.data.user.delegation;

        setUser((prev: any) => ({
          ...prev,
          delegation: updatedDelegation,
        }));

        socket?.emit("judge-delegation", {
          enabled: updatedDelegation,
          courtRoomId: res.data.user._id,
        });

        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "An error occurred",
        { toastId: "delegation error" },
      );
    } finally {
      setLoading(false);
    }
  };
  const handleNavigation = useCallback(
    (item: MenuSidebarItem) => {
      onClose();
      setActiveItem(item.id);

      if (item.path === "/judge/causelist") {
        setCauseListShow(!causeListShow);
        return;
      }

      if (item.path === "/judge/reader-delegation") {
        handleDelegationToggle();
        return;
      }

      if (item.path) navigate(item.path);
    },
    [navigate, causeListShow],
  );

  /* ---------------- STYLE ---------------- */
  const getItemClass = (isActive: boolean, isDelegation: boolean) => {
    let stateClass = "";

    if (isDelegation) {
      stateClass = "text-blue-600";
    } else if (isActive) {
      stateClass =
        "bg-blue-50 text-blue-700 border-l-4 border-blue-600 font-medium";
    } else {
      stateClass =
        "text-gray-700 hover:bg-gray-50 hover:text-blue-600 font-medium";
    }

    return clsx(
      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
      stateClass,
    );
  };

  /* ---------------- RENDER ---------------- */

  const renderChild = (child: MenuSidebarItem) => {
    const Icon = child.icon;
    const isActive = child.id === childActiveItem;

    return (
      <li key={child.id}>
        <button
          onClick={() => {
            onClose();
            setChildActiveItem(child.id);
            navigate(child.path!);
          }}
          className={getItemClass(isActive, false)}
        >
          <Icon size={18} />
          <span>{child.label}</span>
        </button>
      </li>
    );
  };

  const renderItem = (item: any) => {
    const Icon = item.icon;
    const isActive = activeItem === item.id;
    const isExpanded = expandedItem === item.id;
    const isDelegation =
      user?.delegation && item.path === "/judge/reader-delegation";

    if (item.children) {
      return (
        <li key={item.id}>
          <button
            onClick={() => setExpandedItem(isExpanded ? "" : item.id)}
            className={getItemClass(isActive, isDelegation || false)}
          >
            <Icon size={20} />
            <span>{item.label}</span>
            <span className="ml-auto">
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </span>
          </button>

          {isExpanded && (
            <ul className="ml-2 mt-1 space-y-1">
              {item.children.map(renderChild)}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.id}>
        <button
          onClick={() => handleNavigation(item)}
          className={getItemClass(isActive, isDelegation || false)}
        >
          <Icon size={20} />
          <span>{item.label}</span>
        </button>
      </li>
    );
  };

  /* ---------------- UI ---------------- */

  return (
    <div
      className={clsx(
        "fixed top-0 left-0 h-full w-64 bg-white shadow-lg transition-transform duration-300 z-50",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0 lg:shadow-none lg:border-r lg:border-gray-200",
      )}
    >
      <div className="flex flex-col h-full">
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <img src="/logo.png" alt="nyaynet" className="w-14 h-16" />
          <button onClick={onClose} className="lg:hidden">
            <X size={20} />
          </button>
        </div>

        {/* NAV */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <ul className="space-y-2">{sidebarItems.map(renderItem)}</ul>
        </nav>

        {/* FOOTER */}
        <div className="px-4 py-4 border-t border-gray-200">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100"
          >
            <LogOut size={20} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
